import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Ship, Package, FileCheck2, Anchor } from "lucide-react";

export const PORT_DETAILS = {
  CNQIN: {
    lanes: ["China → Mediterranean & North Europe base-metal flows", "China → Gulf and Indian subcontinent semis", "Intra-Asia feeder connections via Busan & Singapore"],
    cargo: "Outbound aluminum extrusions and semis (HS 7604), copper semis and rod; inbound containerized units for northern China processors.",
    compliance: "Inbound non-ferrous scrap to China requires strict pre-shipment inspection and GB-standard classification. Outbound semis need accurate HS 7604/7403 declarations and mill certificates.",
  },
  ILASH: {
    lanes: ["East Mediterranean short-sea network — Türkiye, Greece, Italy", "Mainline Med services via Malta & Valencia transhipment", "Import gateway for the Tel Aviv industrial corridor"],
    cargo: "Israel's principal southern container gateway — inbound metals, machinery and raw materials for the Tel Aviv–Haifa industrial belt; outbound semis and recycled metals.",
    compliance: "Israeli imports require a registered importer entity and standards-institute conformity for metal products. Scrap cargoes face environmental ministry oversight and pre-arrival documentation checks.",
  },
  AEJEA: {
    lanes: ["Gulf ↔ Indian subcontinent scrap corridors", "Far East → Gulf container services", "Re-export hub for Middle East & Africa"],
    cargo: "Consolidation hub for mixed non-ferrous scrap (Zorba, copper nodules) and Gulf-region aluminum products; strong re-export trade.",
    compliance: "JAFZA free-zone handling simplifies re-export. Scrap moves require Dubai Municipality environmental compliance and accurate HS 7404/7602 declarations.",
  },
  INMUN: {
    lanes: ["Far East → West India mainline services", "India → Europe & Mediterranean exports", "Gulf → India scrap backhaul lanes"],
    cargo: "Major gateway for Indian copper & aluminum scrap imports feeding Gujarat's secondary smelting cluster; outbound ingot and semis.",
    compliance: "Indian scrap imports require Pre-Shipment Inspection Certificates (PSIC) and are subject to port-of-entry metal-scrap scrutiny. HS 7404/7602 duty structures apply.",
  },
  INNSA: {
    lanes: ["Europe & US → Mumbai gateway services", "Far East ↔ West India mainline loops", "Coastal feeder links to Mundra & Pipavav"],
    cargo: "India's busiest container gateway — non-ferrous scrap imports for Maharashtra processors and refined metal distribution.",
    compliance: "PSIC mandatory for metallic scrap; containers may face 100% scanning for radioactive and arms/ammunition contamination checks.",
  },
  THLCH: {
    lanes: ["Intra-Asia loops: Thailand ↔ China, Japan, Korea", "Thailand → transpacific head-haul services", "Feeder links via Singapore & Port Klang"],
    cargo: "Copper and aluminum scrap imports feeding Thai secondary smelters and rod mills; outbound castings and semis.",
    compliance: "Thai scrap imports need factory/import licences and DIW environmental approvals. Strict inspection on mixed loads — clean single-grade containers move fastest.",
  },
  TRIST: {
    lanes: ["Med & Black Sea short-sea network", "Europe → Türkiye rail/road-sea intermodal", "Türkiye → Middle East & North Africa exports"],
    cargo: "Copper scrap and blister feeding Turkish rod mills and refineries; aluminum extrusion billet flows for the construction sector.",
    compliance: "Turkish scrap imports require environmental ministry registration of the importer. Customs valuation on copper units is closely monitored — assay-backed invoicing recommended.",
  },
};

export default function PortModal({ port, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const details = port ? PORT_DETAILS[port.code] : null;

  return (
    <AnimatePresence>
      {port && details && (
        <motion.div
          data-testid="port-detail-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-[#0A0D12]/85 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 48, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 32, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-[#111620] border border-[#E06D28]/30 rounded-md p-7 sm:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-[#E06D28]" />
            <span className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-[#E06D28]" />

            <button
              data-testid="port-detail-close"
              onClick={onClose}
              aria-label="Close port details"
              className="absolute top-5 right-5 text-[#64748B] hover:text-[#F07D38] transition-colors"
            >
              <X size={22} />
            </button>

            <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#F07D38]">UN/LOCODE {port.code}</div>
            <h3 className="font-display font-black text-3xl sm:text-4xl text-[#F4F6F8] mt-2">{port.name}</h3>
            <div className="inline-block font-mono-tech text-xs font-semibold text-[#0A0D12] bg-[#F3B700] px-3 py-1.5 rounded-sm mt-4">
              {port.region}
            </div>

            <div className="mt-8 space-y-6">
              <div className="flex gap-4">
                <Ship size={18} className="text-[#E06D28] shrink-0 mt-0.5" />
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1.5">Key Trade Lanes</div>
                  <ul className="space-y-1.5">
                    {details.lanes.map((l) => (
                      <li key={l} className="flex items-start gap-2 text-sm text-[#94A3B8] leading-relaxed">
                        <span className="mt-[7px] w-1.5 h-1.5 rotate-45 bg-[#E06D28] shrink-0" />
                        {l}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="flex gap-4">
                <Package size={18} className="text-[#E06D28] shrink-0 mt-0.5" />
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1.5">Cargo Notes</div>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{details.cargo}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <FileCheck2 size={18} className="text-[#F3B700] shrink-0 mt-0.5" />
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1.5">HS Compliance & Customs</div>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{details.compliance}</p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-[#283244] flex items-center gap-3 text-[11px] text-[#64748B] font-mono-tech">
              <Anchor size={13} className="text-[#E06D28]" />
              Indicative routing intelligence — transit terms confirmed per booking & carrier schedule
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
